import { PropsWithChildren } from 'react'
import {
  Alert,
  Button,
  ButtonGroup,
  Column,
  HeaderFooterPage,
  Heading,
  InputCode,
  Row,
  SplitRow,
} from '@skedulo/breeze-ui-react'
import { buildPlatformUrl, hasPermission } from '../../core/globals'
import { ErrorLevel, PagesPermission, UIErrorCode } from '../constants'
import { PlatformError } from '../utils'
import { ErrorDetail } from './error-detail'
import type { ErrorFallbackProps } from './error-fallback'

export type DebuggableErrorFallbackProps =
  ErrorFallbackProps<ErrorLevel.TEMPLATE>

/**
 * Renders a more detailed view of an error fallback. Currently supports only
 * Level 2 (Template) errors, but could be extended to support more scenarios in
 * future. The primary use case is when a template render fails in the Nunjucks
 * environment (e.g. syntax errors), providing a detailed reason for failure and
 * a direct link to edit the broken template.
 */
export const DebuggableErrorFallback = ({
  code = UIErrorCode.UNKNOWN,
  traceId,
  error,
  reset,
}: PropsWithChildren<DebuggableErrorFallbackProps>) => {
  const canModifyPages = hasPermission(PagesPermission.MODIFY)

  const handleReloadClick = () => {
    reset()
  }

  const templateUid =
    error instanceof PlatformError
      ? ((error.metadata?.templateUid as string) ?? null)
      : null

  const header = (
    <div style={{ padding: 'var(--brz-spacing-6) var(--brz-spacing-5)' }}>
      <Alert type="error">There is a problem loading this page</Alert>
    </div>
  )

  const footer = (
    <SplitRow
      style={{
        padding: 'var(--brz-spacing-5) var(--brz-spacing-6)',
        borderTop: 'var(--brz-border-divider)',
      }}
    >
      <ButtonGroup slot="right">
        <Button buttonType="secondary" onClick={handleReloadClick}>
          Reload page
        </Button>

        {canModifyPages && templateUid && (
          <a href={buildPlatformUrl(`page-edit?uid=${templateUid}`)}>
            <Button>Edit page template</Button>
          </a>
        )}
      </ButtonGroup>
    </SplitRow>
  )

  return (
    <HeaderFooterPage header={header} footer={footer}>
      <Column
        style={{
          '--brz-column-spacing': 'var(--brz-spacing-5)',
          padding: 'var(--brz-spacing-0) var(--brz-spacing-5)',
        }}
      >
        <article>
          <Heading size="base" style={{ marginBottom: 'var(--brz-spacing-2)' }}>
            Error reference
          </Heading>

          <Row style={{ '--brz-row-spacing': 'var(--brz-spacing-2)' }}>
            <ErrorDetail code={code} traceId={traceId} />
          </Row>
        </article>

        <article
          style={{ width: '100%', marginBottom: 'var(--brz-spacing-8)' }}
        >
          <Heading size="base" style={{ marginBottom: 'var(--brz-spacing-2)' }}>
            More information
          </Heading>

          <InputCode
            value={error.stack ?? error.message}
            name="errorMessage"
            size="full"
            mode="json"
            height={17}
            readonly
            hideLineNumbers
            data-testid="error-stack"
          />
        </article>
      </Column>
    </HeaderFooterPage>
  )
}
